import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { StarRating } from '../../components/StarRating';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { providersAPI, requestsAPI, petsAPI } from '../../lib/api';
import { getServiceName, formatPrice, formatDate, formatRelativeTime, getCityName, SERVICE_TYPES, DAYS_OF_WEEK } from '../../lib/constants';
import { CheckCircle, MapPin, Clock, CalendarBlank, ChatCircle, ArrowLeft, Star, Dog, PawPrint, Plus, Trash } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

function ReviewCard({ review }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar>
            <AvatarImage src={review.client?.picture} />
            <AvatarFallback>{review.client?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">{review.client?.name}</h4>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(review.created_at)}
              </span>
            </div>
            <StarRating rating={review.rating} size="sm" />
            {review.comment && (
              <p className="mt-2 text-sm text-muted-foreground">{review.comment}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProviderDetailPage() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myPets, setMyPets] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  
  // Request form state
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPets, setSelectedPets] = useState([]);
  const [address, setAddress] = useState('');
  const [observations, setObservations] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preferredSchedule, setPreferredSchedule] = useState([]);
  const [scheduleNotes, setScheduleNotes] = useState('');

  const addScheduleSlot = () => {
    setPreferredSchedule(prev => [...prev, { day: 0, start: '09:00', end: '18:00' }]);
  };

  const updateScheduleSlot = (index, field, value) => {
    setPreferredSchedule(prev => prev.map((s, i) => i === index ? { ...s, [field]: field === 'day' ? parseInt(value) : value } : s));
  };

  const removeScheduleSlot = (index) => {
    setPreferredSchedule(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [providerData, petsData] = await Promise.all([
          providersAPI.getProvider(providerId),
          petsAPI.getMyPets().catch(() => [])
        ]);
        setProvider(providerData);
        setMyPets(petsData);
      } catch (error) {
        toast.error('Erro ao carregar prestador');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [providerId]);

  const handleSubmitRequest = async () => {
    if (!selectedService || !selectedDate || selectedPets.length === 0 || !address) {
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      await requestsAPI.createRequest({
        provider_id: providerId,
        service_type: selectedService,
        start_date: selectedDate.toISOString(),
        end_date: endDate?.toISOString() || null,
        preferred_schedule: preferredSchedule,
        schedule_notes: scheduleNotes || null,
        address,
        city: user?.city || 'lisboa',
        pets: selectedPets,
        observations
      });
      
      toast.success('Pedido enviado com sucesso!');
      setRequestDialogOpen(false);
      navigate('/client/requests');
    } catch (error) {
      toast.error('Erro ao enviar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePetSelection = (petId) => {
    setSelectedPets(prev => 
      prev.includes(petId) 
        ? prev.filter(id => id !== petId)
        : [...prev, petId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-64 bg-muted rounded-2xl" />
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-4 w-full bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <Dog weight="duotone" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Prestador não encontrado</h2>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const providerUser = provider.user || {};
  const reviews = provider.reviews || [];
  const services = provider.services || [];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header Card */}
          <Card className="overflow-hidden mb-6">
            <div className="relative h-48 bg-gradient-to-br from-primary/20 to-secondary/20">
              {provider.photos?.[0] && (
                <img 
                  src={provider.photos[0]} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <CardContent className="relative pt-0">
              <div className="flex flex-col sm:flex-row gap-4 -mt-12 sm:-mt-8">
                <Avatar className="w-24 h-24 border-4 border-background">
                  <AvatarImage src={providerUser.picture} alt={providerUser.name} />
                  <AvatarFallback className="text-2xl">{providerUser.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 pt-4 sm:pt-8">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-bold font-outfit">{providerUser.name}</h1>
                        {provider.verified && (
                          <CheckCircle weight="fill" className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {provider.zones?.map(getCityName).join(', ') || 'Portugal'}
                        </span>
                        <StarRating rating={provider.rating || 0} size="sm" showCount count={provider.review_count || 0} />
                      </div>
                    </div>
                    <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full" data-testid="request-service-btn">
                          Pedir Serviço
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Pedir Serviço a {providerUser.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Tipo de Serviço *</label>
                            <Select value={selectedService} onValueChange={setSelectedService}>
                              <SelectTrigger data-testid="select-service">
                                <SelectValue placeholder="Selecione um serviço" />
                              </SelectTrigger>
                              <SelectContent>
                                {services.map((service) => (
                                  <SelectItem key={service.type} value={service.type}>
                                    {getServiceName(service.type)} - {formatPrice(service.price_per_hour || service.price_per_day)}/{service.price_per_hour ? 'h' : 'dia'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <label className="text-sm font-medium mb-2 block">Data de Início *</label>
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={setSelectedDate}
                              locale={pt}
                              disabled={(date) => date < new Date()}
                              className="rounded-lg border"
                            />
                          </div>

                          {(selectedService === 'hospedagem' || selectedService === 'dog_sitting') && (
                            <div>
                              <label className="text-sm font-medium mb-2 block">Data de Fim</label>
                              <Calendar
                                mode="single"
                                selected={endDate}
                                onSelect={setEndDate}
                                locale={pt}
                                disabled={(date) => date < (selectedDate || new Date())}
                                className="rounded-lg border"
                              />
                            </div>
                          )}

                          <div>
                            <label className="text-sm font-medium mb-2 block">Selecione os Animais *</label>
                            {myPets.length === 0 ? (
                              <Card>
                                <CardContent className="py-4 text-center">
                                  <PawPrint className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                  <p className="text-sm text-muted-foreground">
                                    Ainda não tem animais registados
                                  </p>
                                  <Button 
                                    variant="link" 
                                    onClick={() => navigate('/client/pets')}
                                    className="mt-2"
                                  >
                                    Adicionar Animal
                                  </Button>
                                </CardContent>
                              </Card>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {myPets.map((pet) => (
                                  <Card 
                                    key={pet.pet_id}
                                    className={`cursor-pointer transition-all ${
                                      selectedPets.includes(pet.pet_id) 
                                        ? 'border-primary bg-primary/5' 
                                        : ''
                                    }`}
                                    onClick={() => togglePetSelection(pet.pet_id)}
                                    data-testid={`select-pet-${pet.pet_id}`}
                                  >
                                    <CardContent className="p-3 flex items-center gap-2">
                                      <Avatar className="w-10 h-10">
                                        <AvatarImage src={pet.photo} />
                                        <AvatarFallback>{pet.name?.charAt(0)}</AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <p className="font-medium text-sm">{pet.name}</p>
                                        <p className="text-xs text-muted-foreground">{pet.breed || pet.species}</p>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium mb-2 block">Morada *</label>
                            <Input
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              placeholder="Rua, número, código postal"
                              data-testid="input-address"
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium">Horário Preferido</label>
                              <Button variant="outline" size="sm" onClick={addScheduleSlot} className="rounded-full h-7 text-xs" data-testid="add-schedule-slot-btn">
                                <Plus className="w-3 h-3 mr-1" /> Dia
                              </Button>
                            </div>
                            {preferredSchedule.length > 0 ? (
                              <div className="space-y-2">
                                {preferredSchedule.map((slot, index) => (
                                  <div key={index} className="flex items-center gap-2 text-sm" data-testid={`client-schedule-slot-${index}`}>
                                    <Select value={String(slot.day)} onValueChange={(v) => updateScheduleSlot(index, 'day', v)}>
                                      <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {DAYS_OF_WEEK.map(d => <SelectItem key={d.id} value={String(d.id)}>{d.short}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                    <Input type="time" value={slot.start} onChange={(e) => updateScheduleSlot(index, 'start', e.target.value)} className="w-24 h-8 text-xs" />
                                    <span className="text-xs text-muted-foreground">-</span>
                                    <Input type="time" value={slot.end} onChange={(e) => updateScheduleSlot(index, 'end', e.target.value)} className="w-24 h-8 text-xs" />
                                    <Button variant="ghost" size="icon" onClick={() => removeScheduleSlot(index)} className="h-7 w-7 text-destructive">
                                      <Trash className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">Opcional: indique os dias/horários que prefere</p>
                            )}
                            {preferredSchedule.length > 0 && (
                              <Input
                                value={scheduleNotes}
                                onChange={(e) => setScheduleNotes(e.target.value)}
                                placeholder="Notas sobre o horário (ex: flexível, preferência manhã...)"
                                className="mt-2 text-sm"
                                data-testid="schedule-notes-input"
                              />
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium mb-2 block">Observações</label>
                            <Textarea
                              value={observations}
                              onChange={(e) => setObservations(e.target.value)}
                              placeholder="Informações adicionais sobre o seu animal ou o serviço..."
                              rows={3}
                              data-testid="input-observations"
                            />
                          </div>

                          <Button
                            onClick={handleSubmitRequest}
                            disabled={submitting || !selectedService || !selectedDate || selectedPets.length === 0 || !address}
                            className="w-full rounded-full"
                            data-testid="submit-request-btn"
                          >
                            {submitting ? 'A enviar...' : 'Enviar Pedido'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              {provider.bio && (
                <p className="mt-6 text-muted-foreground">{provider.bio}</p>
              )}
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="services" className="space-y-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="services">Serviços</TabsTrigger>
              <TabsTrigger value="reviews">Avaliações ({reviews.length})</TabsTrigger>
              <TabsTrigger value="photos">Fotos</TabsTrigger>
            </TabsList>

            <TabsContent value="services">
              <div className="grid gap-4">
                {services.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <p className="text-muted-foreground">Nenhum serviço definido</p>
                    </CardContent>
                  </Card>
                ) : (
                  services.map((service, index) => (
                    <Card key={service.type}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{getServiceName(service.type)}</h3>
                          <p className="text-sm text-muted-foreground">
                            {SERVICE_TYPES.find(s => s.id === service.type)?.description}
                          </p>
                        </div>
                        <div className="text-right">
                          {service.price_per_hour && (
                            <p className="font-bold text-primary">{formatPrice(service.price_per_hour)}/h</p>
                          )}
                          {service.price_per_day && (
                            <p className="font-bold text-primary">{formatPrice(service.price_per_day)}/dia</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="reviews">
              <div className="space-y-4">
                {reviews.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <Star weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Ainda não há avaliações</p>
                    </CardContent>
                  </Card>
                ) : (
                  reviews.map((review) => (
                    <ReviewCard key={review.review_id} review={review} />
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="photos">
              {provider.photos?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {provider.photos.map((photo, index) => (
                    <div key={photo} className="aspect-square rounded-2xl overflow-hidden">
                      <img 
                        src={photo} 
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">Nenhuma foto disponível</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
